import type React from 'react';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';

import CalculatorWidget from './CalculatorWidget';
import TimerWidget from './TimerWidget';

import type { InteractiveWidgetType } from '@/shared/types/slide';

// ---------------------------------------------------------------------------
// Widget Registry
//
// Maps widget type identifiers to their metadata and renderer component.
// To add a new interactive widget:
//   1. Add the type to `InteractiveWidgetType` in shared/types/slide.ts
//   2. Create the widget component (see CalculatorWidget for the interface)
//   3. Register it in `WIDGET_REGISTRY` below
// ---------------------------------------------------------------------------

export interface InteractiveWidgetProps {
  config: Record<string, unknown>;
  /** When true the widget is fully interactive; when false it shows a static preview. */
  interactive: boolean;
  width: number;
  height: number;
  /** Persist a config change back to the slide element data. */
  onConfigChange?: (patch: Record<string, unknown>) => void;
}

export interface WidgetRegistryEntry {
  type: InteractiveWidgetType;
  name: string;
  description: string;
  icon: React.ElementType;
  defaultWidth: number;
  defaultHeight: number;
  defaultConfig: Record<string, unknown>;
  component: React.ComponentType<InteractiveWidgetProps>;
}

const WIDGET_REGISTRY: Record<InteractiveWidgetType, WidgetRegistryEntry> = {
  calculator: {
    type: 'calculator',
    name: 'Calculator',
    description: 'A simple four-function calculator',
    icon: CalculateOutlinedIcon,
    defaultWidth: 240,
    defaultHeight: 320,
    defaultConfig: {},
    component: CalculatorWidget,
  },
  timer: {
    type: 'timer',
    name: 'Timer',
    description: 'A countdown timer with preset durations',
    icon: TimerOutlinedIcon,
    defaultWidth: 320,
    defaultHeight: 240,
    defaultConfig: { durationSeconds: 300 },
    component: TimerWidget,
  },
};

export function getWidget(type: string): WidgetRegistryEntry | undefined {
  return WIDGET_REGISTRY[type as InteractiveWidgetType];
}

export function getAllWidgets(): WidgetRegistryEntry[] {
  return Object.values(WIDGET_REGISTRY);
}
